<template>
  <div class="bg-gray-50">
    <div class="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-12 lg:grid-cols-[240px_1fr] lg:px-8">
      <aside class="self-start rounded-3xl bg-white p-4 shadow-card ring-1 ring-gray-100 lg:sticky lg:top-20">
        <p class="text-xs font-semibold uppercase tracking-wide text-muted">Privacy</p>
        <div class="mt-3 space-y-2 text-sm font-semibold text-ink">
          <button
            v-for="section in sections"
            :key="section.id"
            class="flex w-full items-center justify-between rounded-2xl px-3 py-2 transition"
            :class="activeSection === section.id ? 'bg-brand/10 text-brand' : 'hover:bg-gray-50'"
            @click="scrollToSection(section.id)"
          >
            <span>{{ section.title }}</span>
            <span v-if="activeSection === section.id" class="text-xs text-brand">•</span>
          </button>
        </div>
      </aside>

      <main class="rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
        <div class="space-y-2 border-b border-gray-100 pb-4">
          <p class="text-xs font-semibold uppercase tracking-wide text-muted">Updated July 2024</p>
          <h1 class="text-3xl font-semibold text-ink">Invi8 Privacy Policy</h1>
          <p class="text-sm text-muted">How we collect, use, and protect your information across invites, vendors, and events.</p>
        </div>

        <div class="mt-6 space-y-12">
          <section
            v-for="section in sections"
            :id="section.id"
            :key="section.id"
            class="space-y-3 scroll-mt-20"
            :ref="el => registerSection(el, section.id)"
          >
            <h2 class="text-xl font-semibold text-ink">{{ section.title }}</h2>
            <p v-for="(para, idx) in section.content" :key="idx" class="text-sm text-muted">
              {{ para }}
            </p>
            <ul v-if="section.list" class="list-disc space-y-2 pl-5 text-sm text-muted">
              <li v-for="(item, idx) in section.list" :key="idx">{{ item }}</li>
            </ul>
          </section>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'

const sections = [
  {
    id: 'data-we-collect',
    title: 'Data we collect',
    content: [
      'We collect information you provide directly (account, event details, invitees), and automatically (device info, usage logs, approximate location for search relevance).',
      'If you connect third-party services (calendars, messaging, payments), we store tokens and metadata needed to operate those features.'
    ],
    list: [
      'Account: name, email, phone, password hash',
      'Event: titles, dates, venues, guest lists, RSVP responses',
      'Vendors: preferences, saved vendors, holds, chat history'
    ]
  },
  {
    id: 'how-we-use',
    title: 'How we use data',
    content: [
      'To operate invites, search, holds, RSVPs, and messaging between hosts and vendors.',
      'To improve recommendations, detect abuse, and support customer service.',
      'To send transactional updates about events, invites, and vendor status.'
    ]
  },
  {
    id: 'sharing',
    title: 'Sharing & vendors',
    content: [
      'We share only what’s needed with vendors you interact with (e.g., event basics, requested dates, contact for confirmations).',
      'Third-party providers (payments, analytics, storage) act on our behalf under contractual safeguards.',
      'We do not sell personal data.'
    ]
  },
  {
    id: 'your-controls',
    title: 'Your controls',
    content: [
      'You can update or delete your account, edit invites, and manage guest lists at any time.',
      'Opt out of marketing emails while still receiving transactional notifications.',
      'Request export or deletion of your data by contacting privacy@invi8.com.'
    ]
  },
  {
    id: 'security',
    title: 'Security',
    content: [
      'We use encryption in transit, least-privilege access controls, and monitoring for unusual activity.',
      'Backups are encrypted and retained for disaster recovery.'
    ]
  },
  {
    id: 'contact',
    title: 'Contact',
    content: [
      'Questions about this policy or your data? Reach us at privacy@invi8.com or through the contact page.'
    ]
  }
]

const activeSection = ref('data-we-collect')
const sectionRefs = reactive({})
let observer = null
const route = useRoute()

const registerSection = (el, id) => {
  if (el) sectionRefs[id] = el
}

const scrollToSection = (id) => {
  const el = sectionRefs[id]
  if (!el) return
  el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  activeSection.value = id
}

const handleIntersect = (entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      activeSection.value = entry.target.id
    }
  })
}

onMounted(() => {
  observer = new IntersectionObserver(handleIntersect, {
    root: null,
    rootMargin: '-20% 0px -60% 0px',
    threshold: [0.1, 0.25, 0.5]
  })
  Object.values(sectionRefs).forEach((el) => observer.observe(el))
  window.addEventListener('hashchange', syncHash)
  syncHash()
})

onUnmounted(() => {
  if (observer) {
    Object.values(sectionRefs).forEach((el) => observer.unobserve(el))
    observer.disconnect()
  }
  window.removeEventListener('hashchange', syncHash)
})

const syncHash = () => {
  const hash = route.hash?.replace('#', '')
  if (hash && sectionRefs[hash]) {
    scrollToSection(hash)
  }
}
</script>
